// geschuetztesBild.js — Wettkämpferfotos in der Verwaltung (21.10.2026)
//
// /uploads/wk-foto-… ist gesperrt (backend/utils/uploadSperre.js). Die Listen
// liefern als `foto` die geprüfte Adresse GET /api/wettkaempfer/:id/foto/bild
// (backend/utils/fotoFreigabe.js, verwaltungsBildAdresse). Die verlangt eine
// Anmeldung, und ein <img src> oder CSS-Hintergrund schickt kein Token. Darum
// wie bei der Fotogalerie (components/fotos/Fotogalerie.jsx): fetch mit
// Anmeldung, daraus eine blob:-Adresse. Hier zentral und zwischengespeichert,
// damit ein Turnierbaum mit 32 Startern nicht bei jedem Neuzeichnen 32 Mal fragt.
//
// Hallenbildschirme (ohne Anmeldung) gehen NICHT hierüber, sondern über
// /api/hallenbild mit dem Hallenschlüssel (lib/hallenbild.js).
import React, { useEffect, useState } from 'react';

const speicher = new Map(); // Adresse → { versprechen, blobUrl, fehler }
const HOECHSTENS = 300;     // ältere blob:-Adressen werden freigegeben

/** Nur eigene, geprüfte Adressen werden mit Anmeldung geholt. */
export const istGeschuetzt = (url) => typeof url === 'string' && /^\/api\/wettkaempfer\/\d+\/foto\/bild(\?|$)/.test(url);

function aufraeumen() {
  while (speicher.size > HOECHSTENS) {
    const [aeltester, e] = speicher.entries().next().value;
    if (e.blobUrl) URL.revokeObjectURL(e.blobUrl);
    speicher.delete(aeltester);
  }
}

/** Holt das Bild einmal; Ergebnis: blob:-Adresse oder Fehler (fetch wirft bei 4xx nicht: res.ok). */
export function holeGeschuetztesBild(url) {
  if (!istGeschuetzt(url)) return Promise.reject(new Error('Keine geprüfte Bildadresse'));
  const da = speicher.get(url);
  if (da) return da.versprechen;
  const eintrag = { versprechen: null, blobUrl: null, fehler: null };
  eintrag.versprechen = fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` } })
    .then((r) => {
      if (!r.ok) throw new Error(`Foto nicht ladbar (${r.status})`);
      return r.blob();
    })
    .then((b) => { eintrag.blobUrl = URL.createObjectURL(b); return eintrag.blobUrl; })
    .catch((e) => {
      eintrag.fehler = e;
      // Fehlschlag nicht für immer merken (z. B. Anmeldung erneuert): nach 30 s neu versuchen
      setTimeout(() => { if (speicher.get(url) === eintrag) speicher.delete(url); }, 30000);
      throw e;
    });
  speicher.set(url, eintrag);
  aufraeumen();
  return eintrag.versprechen;
}

/** Schon geladen? Dann die blob:-Adresse, sonst null (ohne zu warten). */
export function geschuetztesBildSofort(url) {
  return speicher.get(url)?.blobUrl || null;
}

/** Vorab holen (z. B. alle Starter eines Baums); Fehler werden still verworfen. */
export function geschuetzteBilderVorladen(urls) {
  for (const u of urls || []) if (istGeschuetzt(u)) holeGeschuetztesBild(u).catch(() => {});
}

/** React: blob:-Adresse zu einer geprüften Adresse, null solange sie lädt oder fehlt. */
export function useGeschuetztesBild(url) {
  const [src, setSrc] = useState(() => (istGeschuetzt(url) ? geschuetztesBildSofort(url) : null));
  const [fehler, setFehler] = useState(false);
  useEffect(() => {
    let aus = false;
    setFehler(false);
    if (!istGeschuetzt(url)) { setSrc(null); return undefined; }
    const sofort = geschuetztesBildSofort(url);
    setSrc(sofort);
    if (!sofort) {
      holeGeschuetztesBild(url)
        .then((b) => { if (!aus) setSrc(b); })
        .catch(() => { if (!aus) { setSrc(null); setFehler(true); } });
    }
    return () => { aus = true; };
  }, [url]);
  return { src, fehler };
}

/**
 * <img> für ein Wettkämpferfoto der Verwaltung. Solange es lädt oder wenn es
 * fehlt: `ersatz` (z. B. Platzhalter), sonst nichts.
 */
export function GeschuetztesBild({ src, alt = '', ersatz = null, ...rest }) {
  const { src: blob } = useGeschuetztesBild(src);
  if (!blob) return ersatz;
  return <img src={blob} alt={alt} {...rest} />;
}
